import { memo, useEffect, useState } from "react"

import { queryCache, useQuery } from "react-query"

import { CellProps } from "components/TreeState"
import { ChangedPlanRecord } from "../types"
import { CircularProgress } from "@material-ui/core"
import { ListCell } from "./ListCell"
import { MultiSelect } from "routes/Base/Admin/components/MultiSelect"
import { css } from "@emotion/css"
import { fetchPlanTargets } from "api/plans"
import { get } from "lodash"
import { set } from "lodash/fp"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"

export const TargetCell = memo(function _TargetCell(props: CellProps) {
  const { id: planId } = useParams<{ id: string }>()
  const { rowId: id, dataKey, semantic, disabled } = props
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const action = readFrom[id]?.action
  const updatable = !action || action === "UPDATE_ORDER"
  const type = readFrom[id]?.type
  const isMedia = type === "media_type_plan"

  const hasLineItem = tableSource
    .getAllChildren(id)
    ?.some((c) => readFrom[c]?.type === "line_item")

  const mediaHasLi = isMedia && hasLineItem
  const applicable = isMedia || type === "line_item"
  const isList = semantic === "targetB" || isMedia
  const isMulti = semantic === "targetB" && isMedia
  const targetIds = (get(readFrom[id], dataKey) || []) as string[]
  const singleId = targetIds?.[0] || ""
  const [multis, setMultis] =
    useState<Array<{ value: string; label: string; disabled?: boolean }>>()

  const { data, isLoading, updatedAt } = useQuery(
    ["client-targets", planId],
    fetchPlanTargets,
    {
      enabled: !queryCache.getQueryData(["client-targets", planId]),
    },
  )

  useEffect(() => {
    if (!multis) {
      setMultis(() => {
        const u: Array<{ value: string; label: string; disabled?: boolean }> =
          []
        targetIds.forEach((t) => {
          const tr = data?.find((d) => d.id === t)
          if (tr?.id && tr?.name)
            u.push({
              value: tr.id,
              label: tr.name,
              disabled: tr.status !== "ACTIVE",
            })
        })
        return u
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  if (!applicable) return <span />
  if (isLoading) return <CircularProgress size="13" />

  if (!isList && !isMulti) {
    const tName = data?.find((d) => d.id === singleId)?.name || ""

    return <span>{tName}</span>
  }

  const options: Array<{ value: string; label: string; disabled?: boolean }> =
    []

  if (isMedia) {
    data?.forEach(
      (d) =>
        d.id &&
        d.name &&
        options.push({
          value: d.id,
          label: d.name,
          disabled: !d.enabled || d.status !== "ACTIVE",
        }),
    )
  } else if (type === "line_item") {
    const parentId = readFrom[id]?.parentId
    if (parentId) {
      const ancestorId = readFrom[parentId]?.parentId
      if (ancestorId) {
        const ancestorTargetIds = (get(readFrom[ancestorId], dataKey) ||
          []) as string[]
        ancestorTargetIds?.forEach((t) => {
          const tr = data?.find((d) => d.id === t)
          tr?.id &&
            tr.name &&
            options.push({
              value: tr.id,
              label: tr.name,
              disabled: tr.status !== "ACTIVE",
            })
        })
      }
    }
  }

  const isDisabled = disabled || mediaHasLi

  const handleToggle = (
    updated: Array<{ value: string; label: string; disabled?: boolean }>,
  ) => {
    setMultis(
      updated.map((u) => ({
        value: u.value,
        label: u.label,
        disabled: u.disabled,
      })),
    )
  }

  const handleClear = () => {
    setMultis([])
  }

  const onSubmit = () => {
    tableSource.clearError(id)
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    const pld = multis?.map((m) => m.value)
    const vpld = pld?.length ? pld : null
    writeTo[id] = set(`${dataKey}`, vpld, writeTo[id])
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  if (isMulti) {
    return (
      <MultiSelect
        checkValues={() => true}
        items={options}
        selectedValues={multis || []}
        getOptionLabel={(option) => option?.label || ""}
        renderOption={(option) => (
          <div className={styles.option}>
            <span>{option.label}</span>
          </div>
        )}
        onToggleOption={handleToggle}
        onClearOptions={handleClear}
        onBlur={onSubmit}
        disabled={isDisabled}
        rootClassName={styles.root}
        optionClassName={styles.optionWrapper}
        renderInput={(params) => {
          return (
            <div ref={params.InputProps.ref} id={params.id}>
              <input
                {...params.inputProps}
                title={multis?.map((m) => m.label)?.join(", ")}
                className={styles.multiInput}
                value={multis?.map((m) => m.label)?.join(", ")}
              />
            </div>
          )
        }}
      />
    )
  }

  return (
    <ListCell
      {...props}
      options={options}
      selected={singleId}
      disabled={isDisabled}
      normalize={(value: string) => (!!value ? [value] : null)}
    />
  )
})

const styles = {
  root: css`
    .MuiOutlinedInput-notchedOutline,
    .MuiOutlinedInput-notchedOutline:hover,
    .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-width: 0;
    }
  `,
  optionWrapper: css`
    padding-top: 0;
    padding-bottom: 0;
  `,

  option: css`
    font-size: 13px;
    span {
      overflow: hidden;
      max-width: 100%;
      white-space: nowrap;
      text-overflow: ellipsis;
      justify-self: flex-end;
    }
  `,
  multiInput: css`
    width: 100%;
    height: 100%;
    border-color: transparent;
    background-color: transparent;
    text-overflow: ellipsis;
    &:hover,
    &:focus,
    &::selection,
    &:active {
      border-color: transparent;
      background-color: transparent;
      outline: none;
    }
  `,
}
